import { Autocomplete, Box, Grid, TextField, useTheme } from '@mui/material'
import IconButton from 'components/buttons/IconButton'
import { StyledInput } from 'components/popups/styled'
import { fi } from 'date-fns/locale'
import i18n from 'i18n'
import { t } from 'i18next'
import { ChangeEvent, FormEvent, ReactNode, useEffect, useState } from 'react'

export interface InputWithApproveButtonProps {
  hintText: string
  onApprove: (text: string) => void
  leftIcon?: ReactNode
  approveIcon: ReactNode
  onTextChange?: (event: ChangeEvent<HTMLInputElement>) => void
  text?: string
  validate?: (text: string) => boolean
  error?: string
  setError?: (error: string) => void
  inputStyle?: React.CSSProperties
  options?: string[]
  bringFocus?: boolean
  compressedImage?: string
  id?: string
  doNotResevePlace?: boolean
  stopPropagationOnClick?: boolean
}

export default function InputWithApproveButton(
  props: InputWithApproveButtonProps
) {
  const theme = useTheme()
  const [areOptionsAvailable, setAreOptionsAvailable] = useState(false)
  const [error, setError] = useState<string>(props.text ? '' : '')
  const [text, setText] = useState<string>(props.text ?? '')
  const onTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    setError('')
    setText(event.target.value)
    if (props.onTextChange) props.onTextChange(event)
  }

  const approve = (text: string) => {
    setAreOptionsAvailable(false)
    if (props.validate != undefined && !props.validate(props.text ?? text)) {
      setError(`${t('emailNotValid')}`)
      return
    }

    props.onApprove(props.text ?? text)
    setText('')
    setError('')
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (text) {
      if (props.validate != undefined && !props.validate(props.text ?? text)) {
        setError(`${t('emailNotValid')}`)
        return
      }
      props.onApprove(text)
    }
  }

  useEffect(() => {
    setText(props.text ?? '')
  }, [props.text])

  return (
    <form style={{ width: '100%' }} onSubmit={handleSubmit}>
      <Grid
        container
        flexWrap={'nowrap'}
        direction={'row'}
        alignItems={'center'}
      >
        <div style={{ minWidth: 42 }}>{props.leftIcon}</div>
        {props.options ? (
          <Autocomplete
            open={areOptionsAvailable}
            value={text}
            options={props.options ?? []}
            sx={{
              width: '100%',
              border: 'none',
              paddingRight: '4px',
              paddingLeft: '4px',

              '& .MuiAutocomplete-inputRoot': {
                paddingRight: '0 !important'
              },
              '& .MuiAutocomplete-input': {
                paddingRight: '0 !important'
              },
              '& .MuiOutlinedInput-root': {
                // Target the OutlinedInput component
                padding: 0,
                border: 'none',
                fontFamily: 'NotoSans',
                paddingRight: 0,
                paddingLeft: 0
              }
            }}
            onChange={(event, value) => {
              if (value) {
                approve(value)
                setText('')
              } else {
                setText('')
              }
            }}
            onInputChange={(event, value) => {
              if (value) {
                setText(value)
                props.onTextChange?.(event as any)

                const filtered = props.options?.filter((option) =>
                  option.toLowerCase().includes(value.toLowerCase())
                )
                setAreOptionsAvailable((filtered?.length ?? 0) > 0)
              } else {
                setText('')
                setError('')
                setAreOptionsAvailable(false)
              }
            }}
            slotProps={{
              popper: { sx: { zIndex: 9998 } }
            }}
            renderOption={(props, option, state) => (
              <li {...props} style={{ fontSize: '14px' }}>
                {option}
              </li>
            )}
            autoHighlight
            renderInput={(params) => {
              delete params.InputProps.endAdornment
              return (
                <TextField
                  {...params}
                  sx={{
                    '& .MuiInput-underline': {
                      fontSize: 14,
                      fontFamily: 'NotoSans',
                      fontWeight: 400,
                      '&:before': {
                        borderBottom: 'none'
                      },
                      '&:after': {
                        borderBottom: 'none'
                      },
                      '&:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none'
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.primary,
                      height: '30px',
                      width: '100% !important',
                      marginLeft: i18n.dir() === 'ltr' ? '-4px' : undefined,
                      marginRight: i18n.dir() === 'rtl' ? '-4px' : undefined,
                      padding: '0px 6px !important',
                      boxSizing: 'border-box',

                      '&:hover': {
                        backgroundColor: theme.palette.surface.hover,
                        borderRadius: '8px'
                      },
                      '::placeholder': {
                        color: theme.palette.text.secondary,
                        opacity: 1
                      }
                    }
                  }}
                  variant="standard"
                  placeholder={props.hintText}
                />
              )
            }}
          />
        ) : (
          <StyledInput
            id={props.id}
            shouldNotFocus={true}
            bringFocus={props.bringFocus}
            placeholder={props.hintText}
            value={text}
            onChange={onTextChange}
            variant="popup"
            sx={{
              ...props.inputStyle,
              '&:hover': {
                backgroundColor: theme.palette.surface.hover,
                borderRadius: '8px'
              }
            }}
          />
        )}
        {text || props.text ? (
          <div style={{ marginRight: 8, marginLeft: 8 }}>
            <IconButton
              id="submit-location-button"
              type="submit"
              size="xs"
              stopPropagationOnClick={props.stopPropagationOnClick}
              onClick={approve.bind(null, text)}
            >
              {props.approveIcon}
            </IconButton>
          </div>
        ) : (
          <div
            style={{ width: props.doNotResevePlace ? '16px' : '40px' }}
          ></div>
        )}
      </Grid>
      <span
        style={{
          color: 'red',
          fontSize: '12px',
          fontWeight: 300
        }}
      >
        {error}
      </span>
    </form>
  )
}
